import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { EntityOwnerPicker } from '../EntityOwnerPicker/EntityOwnerPicker';
import { EntityTagPicker } from '../EntityTagPicker/EntityTagPicker';
import { EntityKindPicker } from '../EntityKindPicker/EntityKindPicker';
import { EntityTypePicker } from '../EntityTypePicker/EntityTypePicker';
import { WarningPanel } from '@backstage/core-components';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { useLocation } from 'react-router-dom';
import { Searchbar } from '../Searchbar/Searchbar';
import Button from '@mui/material/Button';
import { useCatalogEntitiesDetail } from '../../hooks/useCatalogEntitiesDetail';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useClearEntityFilters } from '@mb.io/core-components';
import { useCatalogContext } from '../../context/CatalogContext';
import { BASE_ROUTE } from '../../constants';
import { CustomEntityUserFilter } from '../../utils/CustomEntityUserFilter/CustomEntityUserFilter';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'start',
    marginBottom: '32px',
    gap: '16px',
    width: '100%',
  },
  picker: {
    width: '25%',
    flexGrow: 1,
  },
  autoComplete: {
    width: '25%',
    flexGrow: 1,
  },
  boxContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  boxColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  boxSearch: {
    display: 'flex',
    gap: '16px',
    width: '100%',
    alignItems: 'center',
  },
  searchbar: {
    width: 'calc(75% - 4px)',
  },
  boxRowMarginTop: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    marginTop: '16px',
    width: '100%',
  },
  filterListIcon: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgb(255, 255, 255, 0.54)'
        : 'rgba(0, 0, 0, 0.54)',
  },
  filterListIconDisabled: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.26)',
  },
  showFiltersButton: {
    minWidth: '150px',
    maxWidth: '70%',
  },
  buttonText: {
    marginLeft: '8px',
  },
  filterButtonGroup: {
    display: 'flex',
    gap: '16px',
  },
  filterButtons: {
    borderRadius: '2px',
  },
  boxBind: {
    display: 'flex',
    width: '100%',
    gap: '56px',
    alignItems: 'center',
  },
  hidden: {
    display: 'none',
  },
}));

export const EntityFiltersAndSearch = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const { pathname, search } = location;

  const { error, appliedFilters, updateFilters } = useCatalogEntitiesDetail();

  const [isOwnerSelected, setIsOwnerSelected] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false); // Keep filters hidden by default
  const [isFiltersEnabled, setIsFiltersEnabled] = useState<boolean>(true);
  const [isClearButtonDisabled, setIsClearButtonDisabled] =
    useState<boolean>(true);
  const [numberOfActiveFilters, setNumberOfActiveFilters] = useState<number>(0);

  const { starredEntities } = useStarredEntities();

  const { searchTerm } = useCatalogContext();
  // Initialize entity filter to be cleared
  const { clearAllFilters, clearSpecifiedFilters } = useClearEntityFilters([
    'kind',
    'type',
    'owners',
    'tags',
    'search',
  ]);

  useEffect(() => {
    if (pathname === `${BASE_ROUTE}/favourites`) {
      const starredEntityRefs = Array.from(starredEntities.values());

      if (starredEntityRefs.length > 0) {
        updateFilters({
          kind: undefined,
          type: undefined,
          owners: undefined,
          tags: undefined,
          user: CustomEntityUserFilter.starred(starredEntityRefs),
        });
      }
    }
  }, [pathname, starredEntities, updateFilters]);

  useEffect(() => {
    if (pathname === `${BASE_ROUTE}/favourites`) {
      // Clear all except search
      clearSpecifiedFilters(['kind', 'type', 'owners', 'tags']);
    }
  }, [clearSpecifiedFilters, pathname]);

  useEffect(() => {
    if (pathname === `${BASE_ROUTE}/favourites`) {
      setShowFilters(false);
      setIsFiltersEnabled(false);
    } else {
      setIsFiltersEnabled(true);
    }
  }, [setShowFilters, setIsFiltersEnabled, pathname]);

  useEffect(() => {
    // If the URL contains any of the filters, show the filters
    const isFiltersShown =
      search.includes('kind') ||
      search.includes('type') ||
      search.includes('owners') ||
      search.includes('tags');
    if (isFiltersShown) {
      setShowFilters(true);
    }
  }, [search]);

  // Effect for enabling/disabling the clear button
  useEffect(() => {
    const getNumberOfActiveFilters = () => {
      const { kind, type, tags } = appliedFilters;

      const activeFilters = [kind, type, tags].map(filter => {
        return (
          filter !== undefined &&
          filter !== null &&
          (typeof filter !== 'object' || Object.keys(filter).length > 0)
        );
      });

      return activeFilters.filter(Boolean).length + (isOwnerSelected ? 1 : 0);
    };

    const activeFilters = getNumberOfActiveFilters();
    setNumberOfActiveFilters(activeFilters);

    if (!isFiltersEnabled) {
      return setIsClearButtonDisabled(true);
    }

    const hasSearchTerm = searchTerm !== '';
    if (!hasSearchTerm) {
      return setIsClearButtonDisabled(activeFilters === 0);
    }

    return setIsClearButtonDisabled(false);
  }, [appliedFilters, isFiltersEnabled, isOwnerSelected, searchTerm]);

  if (error) {
    return (
      <WarningPanel
        severity="error"
        title="Could not load Filters for Entity table."
      >
        {error.message}
      </WarningPanel>
    );
  }

  const renderFilterButtonsGroup = () => {
    const showButtonText = 'Show filters';
    const hideButtonText = 'Hide filters';
    const clearButtonText = 'Clear filters';

    return (
      <div className={classes.filterButtonGroup}>
        <Button
          variant="outlined"
          color="secondary"
          className={`${classes.showFiltersButton} ${classes.filterButtons}`}
          data-testid="filter-button"
          disabled={!isFiltersEnabled}
          onClick={() => {
            setShowFilters(!showFilters);
          }}
        >
          <FilterListIcon
            className={`${classes.filterListIcon} ${!isFiltersEnabled ? classes.filterListIconDisabled : ''}`}
          />
          <div className={classes.buttonText}>
            {showFilters ? hideButtonText : showButtonText}
          </div>
        </Button>
        <Stack spacing={2} direction="row">
          <Badge
            color="info"
            invisible={pathname === `${BASE_ROUTE}/favourites`}
            badgeContent={numberOfActiveFilters}
            data-testid="filters-badge"
          >
            <Button
              disabled={isClearButtonDisabled}
              onClick={() => clearAllFilters()}
              className={`${classes.showFiltersButton} ${classes.filterButtons}`}
              data-testid="clear-filters-button"
              variant="outlined"
              color="secondary"
            >
              <FilterListOffIcon
                className={`${classes.filterListIcon} ${isClearButtonDisabled ? classes.filterListIconDisabled : ''}`}
              />
              <div className={classes.buttonText}>{clearButtonText}</div>
            </Button>
          </Badge>
        </Stack>
      </div>
    );
  };

  const filtersCollection = () => {
    return (
      <Box
        className={`${classes.boxRowMarginTop} ${!showFilters ? classes.hidden : ''}`}
      >
        <div className={classes.picker}>
          <EntityKindPicker data-testid="entity-kind-picker" />
        </div>
        <div className={classes.picker}>
          <EntityTypePicker data-testid="entity-type-picker" />
        </div>
        <div className={classes.autoComplete}>
          <EntityOwnerPicker
            data-testid="entity-owner-picker"
            isMyOwnersOnly={pathname === `${BASE_ROUTE}/owned`}
            mode="owners-only"
            onSelectedOwnersChange={isSelected => {
              setIsOwnerSelected(isSelected);
            }}
          />
        </div>
        <div className={classes.autoComplete}>
          <EntityTagPicker data-testid="entity-tag-picker" />
        </div>
      </Box>
    );
  };

  return (
    <div className={classes.wrapper}>
      <Box className={classes.boxContainer}>
        <Box className={classes.boxBind}>
          <Box className={classes.boxColumn}>
            <Box className={classes.boxSearch}>
              <div className={classes.searchbar}>
                <Searchbar />
              </div>
              <Box>{renderFilterButtonsGroup()}</Box>
            </Box>
            {pathname !== `${BASE_ROUTE}/favourites` && filtersCollection()}
          </Box>
        </Box>
      </Box>
    </div>
  );
};
